import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { BillDto } from '@/api/generated/schemas';
import { BillUploadModal, BillsTable } from '@/components/bills';
import {
  EmptyState,
  ErrorState,
  LoadingSpinner,
  PageHeader,
  PaginationControls,
  SearchInput,
} from '@/components/common';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import { useBills } from '@/queries/bills.queries';
import { useSearchBills } from '@/queries/search.queries';

export const Route = createFileRoute('/bills/')({
  component: Bills,
});

function Bills() {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const pagination = usePaginatedSearch<BillDto>();
  const paginatedQuery = useBills({ cursor: pagination.cursor, pageSize: pagination.pageSize });
  const searchQuery = useSearchBills(pagination.debouncedSearch, pagination.searchPage);
  const {
    data: bills,
    isLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    handleNextPage,
    handlePreviousPage,
    isFetching,
  } = pagination.withQueries({ paginatedQuery, searchQuery });

  return (
    <div className="py-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <PageHeader
          title="Bills"
          description="A list of all your utility bills including provider, amount, and period."
        />

        {/* Search Bar */}
        <div className="w-full sm:max-w-md">
          <SearchInput
            value={pagination.searchQuery}
            onChange={pagination.handleSearchChange}
            placeholder="Search bills..."
          />
        </div>

        {/* Loading State */}
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState title="Failed to load bills" message="Please try again later" />
        ) : bills.length > 0 ? (
          <>
            <BillsTable bills={bills} />
            <PaginationControls
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              isLoading={isFetching}
            />
          </>
        ) : (
          <EmptyState
            icon={PlusIcon}
            title={pagination.searchQuery ? 'No bills found matching your search' : 'No bills uploaded yet'}
            description={
              pagination.searchQuery
                ? 'Try adjusting your search terms'
                : 'Get started by uploading your first utility bill'
            }
            variant={pagination.searchQuery ? 'search' : 'default'}
            action={
              pagination.searchQuery
                ? undefined
                : {
                    label: 'Upload Bills',
                    icon: PlusIcon,
                    onPress: () => setIsUploadModalOpen(true),
                    color: 'primary',
                  }
            }
          />
        )}
      </div>

      {/* Upload Modal */}
      <BillUploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />
    </div>
  );
}
